import React, { useState, useRef, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { FaComment, FaTimes, FaPaperPlane, FaLock } from 'react-icons/fa';
import '../Chatbot.css';

const CANNED_RESPONSES = [
  {
    trigger: /museum|gallery|art/i,
    response: "Great idea! For this destination, I'd recommend visiting the local art museums and galleries. In the full version, I can actually modify your itinerary to include specific museum visits with optimal timing.\n\n**What I can do with a Solo plan:**\n- Add specific museums to your schedule\n- Adjust timing to avoid crowds\n- Suggest nearby restaurants for lunch breaks\n- Reorder your day for the best experience"
  },
  {
    trigger: /restaurant|food|eat|dining|vegetarian|vegan/i,
    response: "I love helping with food recommendations! This destination has amazing local cuisine options.\n\n**With a Solo plan, I can:**\n- Add specific restaurant recommendations\n- Filter by dietary preferences (vegetarian, vegan, halal, etc.)\n- Time meal stops perfectly between activities\n- Suggest hidden local gems off the tourist path"
  },
  {
    trigger: /outdoor|hike|adventure|sport|surf/i,
    response: "Adventure activities are a great way to experience this destination! There are several exciting options nearby.\n\n**With a Solo plan, I can:**\n- Add adventure activities to your itinerary\n- Check seasonal availability\n- Suggest the best time of day for each activity\n- Include equipment rental info and booking links"
  },
  {
    trigger: /hotel|stay|accommodation|sleep/i,
    response: "Finding the right accommodation is key to a great trip! I can see this area has excellent options.\n\n**With a Solo plan, I can:**\n- Recommend hotels near your planned activities\n- Compare budget vs luxury options\n- Suggest areas to stay based on your itinerary\n- Add check-in/check-out times to your schedule"
  },
  {
    trigger: /./,
    response: "That's a great question! I'm Artie, your AI travel assistant. In this demo, I can show you how I work, but my full capabilities require a Solo plan.\n\n**With a Solo plan, I can:**\n- Modify your itinerary in real-time\n- Add or remove attractions\n- Adjust timing and routes\n- Provide personalized local tips\n- Translate your entire itinerary"
  }
];

const formatMessageText = (text) => {
  if (!text) return '';
  const lines = text.split('\n');
  let html = '';
  let inList = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const ulMatch = line.match(/^\s*[-*•]\s+(.*)/);

    if (ulMatch) {
      if (!inList) {
        html += '<ul>';
        inList = true;
      }
      html += `<li>${ulMatch[1]}</li>`;
    } else {
      if (inList) {
        html += '</ul>';
        inList = false;
      }
      if (line.trim() === '') {
        html += '<br/>';
      } else {
        html += `<p>${line}</p>`;
      }
    }
  }
  if (inList) html += '</ul>';
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  return DOMPurify.sanitize(html);
};

const DEMO_MESSAGE_LIMIT = 2;

const DemoChatbot = ({ destination }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [messageCount, setMessageCount] = useState(0);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = () => {
    if (!inputValue.trim() || messageCount >= DEMO_MESSAGE_LIMIT) return;

    const userMessage = inputValue.trim();
    setInputValue('');

    setMessages(prev => [...prev, { type: 'user', content: userMessage }]);

    // Find matching canned response
    const match = CANNED_RESPONSES.find(r => r.trigger.test(userMessage));
    const response = match ? match.response : CANNED_RESPONSES[CANNED_RESPONSES.length - 1].response;

    // Simulate typing delay
    setTimeout(() => {
      setMessages(prev => [...prev, { type: 'ai', content: response }]);
      setMessageCount(prev => prev + 1);
    }, 800);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const isLimitReached = messageCount >= DEMO_MESSAGE_LIMIT;

  return (
    <>
      {!isOpen && (
        <div className="chatbot-toggle-container">
          <button
            className="chatbot-toggle-button"
            onClick={() => setIsOpen(true)}
            title="Chat with Artie - AI Travel Assistant (Demo)"
          >
            <FaComment />
            <span className="chatbot-badge">Artie</span>
          </button>
        </div>
      )}

      {isOpen && (
        <div className="chatbot-window">
          <div className="chatbot-header">
            <div className="chatbot-header-title">
              <span className="chatbot-artie-icon">👨‍✈️</span>
              <h3>Artie</h3>
              <span className="chatbot-subtitle">Demo Mode</span>
            </div>
            <button className="chatbot-close-button" onClick={() => setIsOpen(false)}>
              <FaTimes />
            </button>
          </div>

          <div className="chatbot-restriction-notice" style={{ background: '#fef3c7', borderBottom: '1px solid #fde68a' }}>
            <span className="restriction-icon">&#9432;</span>
            <span style={{ color: '#92400e' }}>Demo mode: {DEMO_MESSAGE_LIMIT - messageCount} message{DEMO_MESSAGE_LIMIT - messageCount !== 1 ? 's' : ''} remaining. Subscribe to Solo plan for unlimited AI chat.</span>
          </div>

          <div className="chatbot-messages">
            {messages.length === 0 && (
              <div className="chatbot-welcome">
                <div className="chatbot-welcome-header">
                  <span className="chatbot-welcome-icon">👨‍✈️</span>
                  <p>Hi! I'm <strong>Artie</strong>, your AI travel assistant. Try asking me about your {destination} trip!</p>
                </div>
                <div className="chatbot-suggestions">
                  <button onClick={() => setInputValue('Can you add a museum to my itinerary?')}>
                    Add a museum
                  </button>
                  <button onClick={() => setInputValue('I want more restaurant options')}>
                    More restaurants
                  </button>
                  <button onClick={() => setInputValue('Add outdoor activities')}>
                    Outdoor activities
                  </button>
                </div>
              </div>
            )}

            {messages.map((message, index) => (
              <div key={index} className={`chatbot-message chatbot-message-${message.type}`}>
                <div
                  className="chatbot-message-content"
                  dangerouslySetInnerHTML={{ __html: formatMessageText(message.content) }}
                />
              </div>
            ))}

            {isLimitReached && (
              <div className="chatbot-message chatbot-message-subscription">
                <div className="chatbot-message-content">
                  <p><FaLock style={{ display: 'inline', marginRight: '6px' }} />
                    You've reached the demo message limit. <strong>Subscribe to the Solo plan</strong> for unlimited AI-powered itinerary modifications!</p>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          <div className="chatbot-input-container">
            <textarea
              className="chatbot-input"
              placeholder={isLimitReached ? 'Demo limit reached — upgrade to Solo plan' : 'Ask about your trip...'}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              rows="2"
              disabled={isLimitReached}
            />
            <button
              className="chatbot-send-button"
              onClick={handleSendMessage}
              disabled={!inputValue.trim() || isLimitReached}
            >
              <FaPaperPlane />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default DemoChatbot;
